import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  QrCodeIcon,
  RectangleStackIcon,
  TicketIcon,
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { useScannerStore } from "../utils/store";

const BRAND = "#1d275f";
const LOGO =
  "https://ticketverz-images.s3.eu-central-1.amazonaws.com/home/1742689091812-ticklogo~white (1).png";

export default function ScannerLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  
  const { user: { data } = {}, setUser,logout } = useScannerStore() || {};

  const navItems = [
    { to: "/scanner/scan", label: "Scan", Icon: QrCodeIcon },
    { to: "/scanner/events", label: "Events", Icon: RectangleStackIcon },
    { to: "/scanner/bookings", label: "Bookings", Icon: TicketIcon },
  ];

  const onLogout = () => {
    try {
      if (typeof logout === "function") logout();
      if (typeof setUser === "function") setUser(null);
    } catch (e) {}
    navigate("/scanner-login");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top header: integrates provided logo + scanner details */}
      <header className="bg-[#1d275f] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden md:flex items-center justify-between h-16">
            {/* left: logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/scanner/scan")}
                className="flex items-center gap-3 focus:outline-none"
              >
                <img src={LOGO} alt="Scanner Logo" className="h-20 w-auto" />
              </button>
            </div>

            {/* right: scanner details + profile dropdown */}
            <div className="flex items-center gap-3 relative">
              <div className="hidden lg:flex items-center gap-3 bg-green-50 text-green-800 px-3 py-1 rounded text-xs">
                <CheckCircleIcon className="w-4 h-4 text-green-700" />
                Camera Ready
              </div>

              <div className="relative">
                <button
                  onClick={() => setProfileOpen((p) => !p)}
                  className="flex items-center gap-2 px-3 py-1 rounded hover:bg-white/10 focus:outline-none"
                >
                  <UserCircleIcon className="w-7 h-7 text-white" />
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium">
                      {data?.name || "Scanner"}
                    </div>
                    <div className="text-xs text-white/80">
                      {data?.email || ""}
                    </div>
                  </div>
                </button>

                <Transition
                  show={profileOpen}
                  as={React.Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <div className="absolute right-0 mt-2 w-44 bg-white text-black rounded shadow-lg py-1 z-50">
                    <div className="px-4 py-2 text-sm font-semibold">
                      {data?.name || "Scanner"}
                    </div>
                    {/* <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate("/scanner/profile");
                      }}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      Profile
                    </button> */}
                    <button
                      onClick={onLogout}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                </Transition>
              </div>
            </div>
          </div>

          {/* mobile compact header */}
          <div className="flex items-center justify-between h-14 md:hidden">
            <div className="flex items-center gap-3">
              <button
                className="p-2 rounded-md"
                onClick={() => setMobileOpen(true)}
              >
                <Bars3Icon className="w-6 h-6 text-white" />
              </button>
              <img src={LOGO} alt="Logo" className="h-10" />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/scanner/scan")}
                className="p-2 rounded-md bg-white/10"
              >
                <QrCodeIcon className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-6 pt-6">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-full md:w-64 sticky top-20 self-start h-[calc(100vh-96px)] pt-6">
          <nav className="space-y-3">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isActive
                      ? "bg-[#edf1ff] text-[#1d275f] font-semibold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`
                }
              >
                <n.Icon className="w-5 h-5" />
                <span>{n.label}</span>
              </NavLink>
            ))}

            <div className="border-t mt-2 pt-3" />
          </nav>
        </aside>

        {/* Main content area */}
        <main className="flex-1 min-h-[60vh] py-6">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile: Drawer sidebar */}
      <Transition show={mobileOpen} as={React.Fragment}>
        <div className="fixed inset-0 z-40 md:hidden">
          <Transition.Child
            as={React.Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileOpen(false)}
            />
          </Transition.Child>

          <Transition.Child
            as={React.Fragment}
            enter="transform transition ease-in-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative w-72 h-full bg-white shadow-xl">
              <div className="p-4 flex items-center justify-between border-b">
                <div className="flex items-center gap-3">
                  <div
                    style={{ backgroundColor: BRAND }}
                    className="w-9 h-9 rounded-md flex items-center justify-center text-white font-semibold"
                  >
                    TV
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    Scanner
                  </div>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-700" />
                </button>
              </div>

              <nav className="p-3 space-y-1">
                {navItems.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                        isActive
                          ? "bg-[#edf1ff] text-[#1d275f] font-semibold"
                          : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <n.Icon className="w-5 h-5" />
                    <span>{n.label}</span>
                  </NavLink>
                ))}

                <div className="border-t my-3" />

                <button
                  onClick={onLogout}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 flex items-center gap-3 text-sm text-gray-700"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </Transition.Child>
        </div>
      </Transition>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex-1 py-2 px-2 flex flex-col items-center justify-center text-xs ${
                    isActive
                      ? "text-[#1d275f]"
                      : "text-gray-500 hover:text-gray-700"
                  }`
                }
              >
                <n.Icon className="w-6 h-6 mb-0.5" />
                <span className="text-xs">{n.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );
}
